import { createSlice } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';

export const updateTestEventSlice = createSlice({
  name: 'updateTestEvent',
  initialState: {
    loading: false,
    progress: null, // 'event' | 'scenarios' | 'attributes' | 'customValues' | 'complete'
    response: {},
    error: null,
  },
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.progress = null;
      state.response = {};
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setProgress: (state, action) => {
      state.progress = action.payload;
    },
    setResponse: (state, action) => {
      state.response = action.payload;
      state.loading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { reset, setLoading, setProgress, setResponse, setError } =
  updateTestEventSlice.actions;

// Main function for updating existing items only
export const updateTestEventAsync = (changes) => async (dispatch) => {
  dispatch(reset());
  dispatch(setLoading(true));

  try {
    const allRequests = [];

    // Step 1: Update the event itself if needed
    if (changes.event?.operation === 'UPDATE' && changes.event.changedFields) {
      dispatch(setProgress('event'));
      const eventData = changes.event.data;

      // Build payload with only changed fields
      const eventPayload = [
        {
          section_data: 'test-events',
          id: eventData.event_db_id,
          ...changes.event.changedFields, // Only include fields that actually changed
        },
      ];

      allRequests.push({
        type: 'UPDATE_EVENT',
        request: axios.put(API_ENDPOINTS.UPDATE_TEST_FLOW, eventPayload),
      });
    }

    // Step 2: Update existing scenarios
    if (changes.scenarios.update.length > 0) {
      dispatch(setProgress('scenarios'));

      const inboundUpdates = changes.scenarios.update
        .filter((e) => e.scenario_type === 'inbound')
        .map((e) => ({
          section_data: 'test-events-inbound',
          id: e.scenario_db_id,
          ...e.changedFields, // Only include changed fields
        }));

      const outboundUpdates = changes.scenarios.update
        .filter((e) => e.scenario_type !== 'inbound')
        .map((e) => ({
          section_data: 'test-events-outbound',
          id: e.scenario_db_id,
          ...e.changedFields, // Only include changed fields
        }));

      if (inboundUpdates.length > 0) {
        allRequests.push({
          type: 'UPDATE_INBOUND_SCENARIOS',
          request: axios.put(API_ENDPOINTS.UPDATE_TEST_FLOW, inboundUpdates),
        });
      }

      if (outboundUpdates.length > 0) {
        allRequests.push({
          type: 'UPDATE_OUTBOUND_SCENARIOS',
          request: axios.put(API_ENDPOINTS.UPDATE_TEST_FLOW, outboundUpdates),
        });
      }
    }

    // Step 3: Update existing attributes
    if (changes.attributes.update.length > 0) {
      dispatch(setProgress('attributes'));

      const inboundAttrUpdates = changes.attributes.update
        .filter((a) => a.scenario_type === 'inbound')
        .map((a) => ({
          section_data: 'test-event-inbound-details',
          id: a.attribute_db_id,
          ...a.changedFields, // Only include changed fields
        }));

      const outboundAttrUpdates = changes.attributes.update
        .filter((a) => a.scenario_type !== 'inbound')
        .map((a) => ({
          section_data: 'test-event-outbound-details',
          id: a.attribute_db_id,
          ...a.changedFields, // Only include changed fields
        }));

      if (inboundAttrUpdates.length > 0) {
        allRequests.push({
          type: 'UPDATE_INBOUND_ATTRIBUTES',
          request: axios.put(API_ENDPOINTS.UPDATE_TEST_FLOW, inboundAttrUpdates),
        });
      }

      if (outboundAttrUpdates.length > 0) {
        allRequests.push({
          type: 'UPDATE_OUTBOUND_ATTRIBUTES',
          request: axios.put(API_ENDPOINTS.UPDATE_TEST_FLOW, outboundAttrUpdates),
        });
      }
    }

    // Step 4: Save dynamic schema-attribute overrides (new-schema scenarios).
    // These never touch insert/update-test-flow; one transactional batch call.
    if (changes.customValues && changes.customValues.length > 0) {
      dispatch(setProgress('customValues'));
      allRequests.push({
        type: 'SAVE_CUSTOM_VALUES',
        request: axios.post(API_ENDPOINTS.SAVE_CUSTOM_VALUES, changes.customValues),
      });
    }

    // Execute all update requests

    if (allRequests.length === 0) {
      dispatch(setProgress('complete'));
      dispatch(
        setResponse({
          message: 'No updates needed',
        }),
      );
      return { success: true, message: 'No updates needed' };
    }

    const responses = await Promise.all(
      allRequests.map(async ({ type, request }) => {
        try {
          const response = await request;
          return { type, success: true, data: response.data };
        } catch (error) {
          console.error(`${type} failed:`, error);
          return { type, success: false, error: error.response?.data?.message || error.message };
        }
      }),
    );

    // Check if any requests failed
    const failures = responses.filter((r) => !r.success);
    if (failures.length > 0) {
      console.error('Some update operations failed:', failures);
      // Continue anyway - partial success is better than total failure
    }

    // SAVE_CUSTOM_VALUES is transactional — if it failed, surface that as a hard failure
    const customValuesFailure = responses.find(
      (r) => r.type === 'SAVE_CUSTOM_VALUES' && !r.success,
    );
    if (customValuesFailure) {
      dispatch(setError(customValuesFailure.error));
      return { success: false, error: customValuesFailure.error };
    }

    dispatch(setProgress('complete'));
    dispatch(
      setResponse({
        message: 'Updates completed',
        details: responses,
      }),
    );

    return {
      success: true,
      responses,
    };
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'Failed to update';
    console.error('Error in update process:', errorMessage);
    dispatch(setError(errorMessage));
    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Selectors
export const selectUpdateTestEventLoading = (state) => state.updateTestEvent.loading;
export const selectUpdateTestEventProgress = (state) => state.updateTestEvent.progress;
export const selectUpdateTestEventResponse = (state) => state.updateTestEvent.response;
export const selectUpdateTestEventError = (state) => state.updateTestEvent.error;

export default updateTestEventSlice.reducer;

// TODO: Handle DELETE operations when endpoint is available
// TODO: Remove hardcoded IDs when API returns real IDs
